<template>
  <l-modal @close="$emit('close')">
    <l-modal-wrapper :direction="transitionDirection">
      <companies-list
        v-if="currentScreen === 'list'"
        key="list"
        :title="t('generate-invoice.title')"
        @modify="handleModify"
        @select="selectCompany"
      />
      <company-editor
        v-else-if="currentScreen === 'editor'"
        key="editor"
        :company="selectedCompany"
        @save="handleSave"
        @back="handleBack"
      />
    </l-modal-wrapper>
  </l-modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LModal, LModalWrapper } from '@last/core-ui/paprika'

import { legalEntitiesApi } from '@/api/client'
import { useConfigStore } from '@/store/config'
import { useTabsStore } from '@/store/tabs'
import { type Company } from '@/types'

import CompaniesList from './CompaniesList.vue'
import CompanyEditor from './CompanyEditor.vue'

const { regenerateBill } = useTabsStore()
const configStore = useConfigStore()
const { t } = useI18n()

const emit = defineEmits<{
  close: []
  newBillId: [string]
}>()

const props = defineProps<{
  tabId: string
  billId: string
}>()

type Screen = 'list' | 'editor'

const currentScreen = ref<Screen>('list')
const transitionDirection = ref<'forward' | 'backward'>('forward')
const selectedCompany = ref<Company>()

async function selectCompany(company: Company) {
  selectedCompany.value = company

  try {
    const result = await legalEntitiesApi.validateLegalEntity({
      validateLegalEntityRequest: {
        taxId: company.taxId,
        companyName: company.name,
        countryCode: configStore.config.locationCountryCode
      }
    })

    if (!result.isValid) {
      transitionDirection.value = 'forward'
      currentScreen.value = 'editor'
      return
    }
  } catch {
    transitionDirection.value = 'forward'
    currentScreen.value = 'editor'
    return
  }

  generateInvoice()
}

function handleModify(company?: Company) {
  selectedCompany.value = company
  transitionDirection.value = 'forward'
  currentScreen.value = 'editor'
}

function handleBack() {
  transitionDirection.value = 'backward'
  currentScreen.value = 'list'
}

function handleSave(company: Company) {
  selectedCompany.value = company
  generateInvoice()
}

function generateInvoice() {
  const billId = regenerateBill({
    tabId: props.tabId,
    billId: props.billId,
    customerCompany: selectedCompany.value!
  })
  emit('newBillId', billId)
  emit('close')
}
</script>
